import { CheckCircle, Circle, Clock, Plus, X, XCircle } from 'lucide-react';
import * as React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  ErrorBar,
  Line,
  Pie,
  PieChart,
  Rectangle,
  XAxis,
  YAxis,
} from 'recharts';
import { Badge } from '@/components/ui/badge';
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { ScenarioOutcome, type ScenarioRun } from '@/lib/types/rome';
import { cn } from '@/lib/utils/cn';
import { useTheme } from '@/providers/ThemeProvider';

import type { SuiteRunGroup } from './SuiteTypes';

// -- Outcome Icons (for individual scenario results) --

export function OutcomeIcon({ outcome, className }: { outcome: ScenarioOutcome | string | null; className?: string }) {
  switch (outcome) {
    case ScenarioOutcome.SUCCESS:
      return <CheckCircle className={cn('size-4 text-teal-500', className)} />;
    case ScenarioOutcome.FAILURE:
      return <XCircle className={cn('size-4 text-rose-500', className)} />;
    case ScenarioOutcome.ERROR:
      return <XCircle className={cn('size-4 text-amber-500', className)} />;
    case ScenarioOutcome.TIMEOUT:
      return <Clock className={cn('size-4 text-amber-500', className)} />;
    case ScenarioOutcome.SKIPPED:
      return <Circle className={cn('size-4 text-gray-400', className)} />;
    case null:
      return <Spinner size="size-4" className={cn('text-blue-500', className)} />;
    default:
      return <Circle className={cn('size-4 text-gray-400', className)} />;
  }
}

export function OutcomeBadge({ outcome }: { outcome: string }) {
  const variant = (() => {
    switch (outcome) {
      case ScenarioOutcome.SUCCESS:
        return 'completed' as const;
      case ScenarioOutcome.FAILURE:
      case ScenarioOutcome.ERROR:
        return 'failed' as const;
      case ScenarioOutcome.TIMEOUT:
        return 'running' as const;
      default:
        return 'scheduled' as const;
    }
  })();

  return (
    <Badge variant={variant} className="text-[10px] uppercase">
      {outcome}
    </Badge>
  );
}

// -- Mini Donut (pass rate pie chart) --

function rateColor(rate: number, isDark = false): string {
  if (isDark) {
    if (rate >= 80) return '#2dd4bf';
    if (rate >= 60) return '#fbbf24';
    return '#fb7185';
  }
  if (rate >= 80) return '#14b8a6';
  if (rate >= 60) return '#f59e0b';
  return '#f43f5e';
}

export function MiniDonut({
  passed,
  total,
  size = 20,
  className,
}: {
  passed: number;
  total: number;
  size?: number;
  className?: string;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const pct = total > 0 ? Math.round((passed / total) * 100) : 0;
  const color = rateColor(pct, isDark);
  const bgColor = isDark ? '#374151' : '#e2e4e8';

  return (
    <div
      className={cn('shrink-0 rounded-full', className)}
      style={{
        width: size,
        height: size,
        background:
          pct >= 100 ? color : pct <= 0 ? bgColor : `conic-gradient(${color} 0% ${pct}%, ${bgColor} ${pct}% 100%)`,
      }}
    />
  );
}

export function MiniDonutWithLabel({
  passed,
  total,
  size = 32,
  className,
}: {
  passed: number;
  total: number;
  size?: number;
  className?: string;
}) {
  const rate = total > 0 ? Math.round((passed / total) * 100) : 0;
  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <MiniDonut passed={passed} total={total} size={size} />
      <span
        className={cn(
          'font-semibold text-[12px] tabular-nums',
          rate >= 80
            ? 'text-teal-600 dark:text-teal-400'
            : rate >= 60
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-rose-600 dark:text-rose-400'
        )}
      >
        {rate}%
      </span>
    </div>
  );
}

// -- Pass/Fail Visualization --

export function PassFailDots({ passed, failed, size = 'sm' }: { passed: number; failed: number; size?: 'sm' | 'md' }) {
  const dotSize = size === 'md' ? 'size-2' : 'size-1.5';
  const dots: React.ReactNode[] = [];
  for (let i = 0; i < passed; i++) {
    dots.push(<div key={`p-${i}`} className={cn(dotSize, 'shrink-0 rounded-full bg-teal-500/80')} />);
  }
  for (let i = 0; i < failed; i++) {
    dots.push(<div key={`f-${i}`} className={cn(dotSize, 'shrink-0 rounded-full bg-rose-500/80')} />);
  }
  return <div className="flex flex-wrap items-center gap-0.5">{dots}</div>;
}

export function PassRateBar({ rate, className }: { rate: number; className?: string }) {
  const color = rate >= 80 ? 'bg-teal-500' : rate >= 60 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className={cn('h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-white/10', className)}>
      <div className={cn('h-full rounded-full transition-all', color)} style={{ width: `${rate}%` }} />
    </div>
  );
}

export function PassRateText({ rate, className }: { rate: number; className?: string }) {
  const color =
    rate >= 80
      ? 'text-teal-600 dark:text-teal-400'
      : rate >= 60
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-rose-600 dark:text-rose-400';
  return <span className={cn('font-mono font-semibold tabular-nums', color, className)}>{rate}%</span>;
}

// -- Status helpers --

export function statusBadgeVariant(status: string): 'running' | 'completed' | 'failed' | 'scheduled' {
  if (status === 'passed') return 'completed';
  if (status === 'failed') return 'failed';
  if (status === 'mixed') return 'failed';
  return 'scheduled';
}

// -- Pass Rate History Chart --

const passRateChartConfig: ChartConfig = {
  passRate: { label: 'Pass Rate' },
};

function barColor(rate: number): string {
  if (rate >= 80) return '#14b8a6';
  if (rate >= 60) return '#f59e0b';
  return '#f43f5e';
}

export function PassRateChart({
  suiteRuns,
  height = 180,
  className,
}: {
  suiteRuns: SuiteRunGroup[];
  height?: number;
  className?: string;
}) {
  const data = React.useMemo(() => {
    const reversed = [...suiteRuns].reverse();
    return reversed.map((suiteRun) => ({
      label: suiteRun.label,
      passRate: suiteRun.passRate,
      passed: suiteRun.passed,
      failed: suiteRun.failed,
      total: suiteRun.total,
    }));
  }, [suiteRuns]);

  if (data.length === 0) return null;

  return (
    <ChartContainer config={passRateChartConfig} className={cn('w-full', className)} style={{ height }}>
      <BarChart data={data} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="2 4" vertical={false} stroke="#d4d4d8" className="dark:stroke-white/[0.06]" />
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          tickMargin={6}
          className="text-[10px] text-gray-500"
          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '10px' }}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={4}
          domain={[0, 100]}
          tickFormatter={(v) => `${v}%`}
          className="text-[10px] text-gray-500"
          width={40}
          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '10px' }}
        />
        <ChartTooltip
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const p = payload[0].payload as Record<string, unknown>;
            const rate = p.passRate as number;
            return (
              <div className="z-50 min-w-[140px] rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-md dark:border-white/10 dark:bg-gray-900">
                <div className="mb-2 border-gray-100 border-b pb-1.5 dark:border-white/10">
                  <span className="font-mono text-[13px] text-gray-900 dark:text-gray-100">{p.label as string}</span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-[12px] text-gray-500 dark:text-gray-400">Pass Rate</span>
                    <span
                      className={cn(
                        'font-medium text-[12px]',
                        rate >= 80
                          ? 'text-teal-600 dark:text-teal-400'
                          : rate >= 60
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-rose-600 dark:text-rose-400'
                      )}
                    >
                      {rate}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-[12px] text-gray-500 dark:text-gray-400">Passed</span>
                    <span className="font-medium text-[12px] text-gray-900 dark:text-gray-100">
                      {p.passed as number}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-[12px] text-gray-500 dark:text-gray-400">Failed</span>
                    <span className="font-medium text-[12px] text-gray-900 dark:text-gray-100">
                      {p.failed as number}
                    </span>
                  </div>
                </div>
              </div>
            );
          }}
        />
        <Bar dataKey="passRate" radius={[3, 3, 0, 0]} maxBarSize={40}>
          {data.map((entry) => (
            <Cell key={entry.label} fill={barColor(entry.passRate)} />
          ))}
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}

// -- Progress Bar --

export function ShimmerProgressBar({ completed, total }: { completed: number; total: number }) {
  const pct = total <= 0 ? 0 : Math.round((completed / total) * 100);
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-white/10">
      <div
        className="h-full rounded-full"
        style={{
          width: `${pct}%`,
          backgroundImage:
            'linear-gradient(90deg, rgba(59,130,246,1) 0%, rgba(96,165,250,1) 50%, rgba(59,130,246,1) 100%)',
          backgroundSize: '200% 100%',
          animation: 'progress-shimmer 2s ease-in-out infinite',
        }}
      />
    </div>
  );
}

// -- Compare View Charts --

const donutConfig: ChartConfig = {
  passed: { label: 'Passed', color: '#14b8a6' },
  failed: { label: 'Failed', color: '#f43f5e' },
  errored: { label: 'Errored', color: '#f59e0b' },
};

export function CompareDonutChart({
  suiteRun,
  size = 120,
  className,
}: {
  suiteRun: SuiteRunGroup;
  size?: number;
  className?: string;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const data = React.useMemo(
    () =>
      [
        { name: 'passed', value: suiteRun.passed, fill: isDark ? '#2dd4bf' : '#14b8a6' },
        { name: 'failed', value: suiteRun.failed, fill: isDark ? '#fb7185' : '#f43f5e' },
        { name: 'errored', value: suiteRun.errored, fill: isDark ? '#fbbf24' : '#f59e0b' },
      ].filter((d) => d.value > 0),
    [suiteRun, isDark]
  );

  return (
    <ChartContainer
      config={donutConfig}
      className={cn('aspect-square', className)}
      style={{ width: size, height: size }}
    >
      <PieChart>
        <ChartTooltip
          content={
            <ChartTooltipContent
              hideLabel
              formatter={(value, name) => (
                <span>
                  {name}: <strong>{String(value)}</strong>
                </span>
              )}
            />
          }
        />
        <Pie data={data} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="85%" strokeWidth={2} />
      </PieChart>
    </ChartContainer>
  );
}

const RUN_COLORS_LIGHT = ['#3b82f6', '#8b5cf6', '#f43f5e', '#f59e0b', '#14b8a6', '#06b6d4'];
const RUN_COLORS_DARK = ['#60a5fa', '#a78bfa', '#fb7185', '#fbbf24', '#2dd4bf', '#22d3ee'];

function formatMetricLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/Ms$/, ' (ms)')
    .replace(/Latency \(ms\)$/, 'Latency (ms)');
}

function metricUnit(key: string): string {
  if (key === 'confidence') return '%';
  if (key.includes('latency') || key.endsWith('_ms')) return 'ms';
  return '';
}

interface MetricDataPoint {
  label: string;
  value: number;
  min: number;
  max: number;
  errorRange: [number, number];
  count: number;
  fill: string;
}

export function CompareMetricsChart({
  suiteRuns,
  height = 200,
  className,
}: {
  suiteRuns: SuiteRunGroup[];
  height?: number;
  className?: string;
}) {
  const { resolvedTheme } = useTheme();
  const runColors = resolvedTheme === 'dark' ? RUN_COLORS_DARK : RUN_COLORS_LIGHT;

  const { metricOptions, dataByMetric } = React.useMemo(() => {
    const metricsSet = new Set<string>();
    const runValues: Record<string, Record<string, number[]>> = {};

    for (const suiteRun of suiteRuns) {
      runValues[suiteRun.label] = {};
      for (const run of suiteRun.runs) {
        const results = run.results as Record<string, unknown> | undefined;
        if (!results) continue;
        for (const [k, v] of Object.entries(results)) {
          if (typeof v !== 'number') continue;
          metricsSet.add(k);
          if (!runValues[suiteRun.label][k]) runValues[suiteRun.label][k] = [];
          runValues[suiteRun.label][k].push(v);
        }
      }
    }

    const options = [...metricsSet].filter((m) => m !== 'detected').sort();
    const byMetric: Record<string, MetricDataPoint[]> = {};

    for (const metric of options) {
      const scale = metric === 'confidence' ? 100 : 1;
      byMetric[metric] = suiteRuns.map((suiteRun, i) => {
        const vals = runValues[suiteRun.label]?.[metric] ?? [];
        const avg = vals.length > 0 ? (vals.reduce((a, b) => a + b, 0) / vals.length) * scale : 0;
        const min = vals.length > 0 ? Math.min(...vals) * scale : 0;
        const max = vals.length > 0 ? Math.max(...vals) * scale : 0;
        const rounded = Math.round(avg * 100) / 100;
        return {
          label: suiteRun.label,
          value: rounded,
          min: Math.round(min * 100) / 100,
          max: Math.round(max * 100) / 100,
          errorRange: [rounded - Math.round(min * 100) / 100, Math.round(max * 100) / 100 - rounded],
          count: vals.length,
          fill: runColors[i % runColors.length],
        };
      });
    }

    return { metricOptions: options, dataByMetric: byMetric };
  }, [suiteRuns, runColors]);

  const [selectedMetric, setSelectedMetric] = React.useState<string>(metricOptions[0] ?? '');

  React.useEffect(() => {
    if (!metricOptions.includes(selectedMetric) && metricOptions.length > 0) {
      setSelectedMetric(metricOptions[0]);
    }
  }, [metricOptions, selectedMetric]);

  if (metricOptions.length === 0) return null;

  const data = dataByMetric[selectedMetric] ?? [];
  const unit = selectedMetric === 'confidence' ? '%' : metricUnit(selectedMetric);
  const chartConfig: ChartConfig = { value: { label: formatMetricLabel(selectedMetric) } };

  return (
    <div className={cn('max-w-lg', className)}>
      <div className="mb-2 flex flex-wrap items-center gap-1">
        {metricOptions.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setSelectedMetric(m)}
            className={cn(
              'cursor-pointer rounded-md px-2.5 py-1 font-medium text-[11px] transition-colors',
              m === selectedMetric
                ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-300 dark:hover:bg-white/20'
            )}
          >
            {formatMetricLabel(m)}
          </button>
        ))}
      </div>
      <ChartContainer config={chartConfig} className="w-full" style={{ height }}>
        <BarChart data={data} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="2 4" vertical={false} stroke="#d4d4d8" className="dark:stroke-white/[0.06]" />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tickMargin={6}
            className="text-[10px] text-gray-500"
            style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '10px' }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={4}
            className="text-[10px] text-gray-500"
            width={50}
            tickFormatter={(v) => `${v}${unit}`}
            style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '10px' }}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                hideIndicator
                formatter={(_value, _name, item) => {
                  const p = item.payload as unknown as MetricDataPoint;
                  return (
                    <div className="flex flex-col gap-0.5 font-mono text-[11px]">
                      <span className="font-medium">{p.label}</span>
                      <span>
                        Avg:{' '}
                        <strong>
                          {p.value}
                          {unit}
                        </strong>
                      </span>
                      <span className="text-muted-foreground">
                        Range: {p.min}
                        {unit} – {p.max}
                        {unit}
                      </span>
                      <span className="text-muted-foreground">n={p.count} scenarios</span>
                    </div>
                  );
                }}
              />
            }
          />
          <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={48}>
            <ErrorBar dataKey="errorRange" width={8} strokeWidth={1.5} stroke="#6b7280" />
            {data.map((entry) => (
              <Cell key={entry.label} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ChartContainer>
    </div>
  );
}

// -- Multi-Panel Metric Dashboard --

interface DashboardPanel {
  id: string;
  metricKey: string;
}

let panelCounter = 0;
function nextPanelId() {
  return `panel-${++panelCounter}`;
}

function discoverMetricOptions(suiteRuns: SuiteRunGroup[]): string[] {
  const keys = new Set<string>();
  for (const suiteRun of suiteRuns) {
    for (const run of suiteRun.runs) {
      const results = run.results as Record<string, unknown> | undefined;
      if (!results) continue;
      for (const [k, v] of Object.entries(results)) {
        if (typeof v === 'number') keys.add(k);
      }
    }
  }
  const sorted = [...keys].filter((m) => m !== 'detected').sort();
  return ['pass_rate', 'duration', ...sorted];
}

function aggregateMetric(suiteRuns: SuiteRunGroup[], metricKey: string, runColors: string[]): MetricDataPoint[] {
  if (metricKey === 'pass_rate') {
    return suiteRuns.map((suiteRun, i) => ({
      label: suiteRun.label,
      value: suiteRun.passRate,
      min: suiteRun.passRate,
      max: suiteRun.passRate,
      errorRange: [0, 0],
      count: suiteRun.total,
      fill: runColors[i % runColors.length],
    }));
  }

  if (metricKey === 'duration') {
    return suiteRuns.map((suiteRun, i) => {
      const vals = suiteRun.runs.map((r: ScenarioRun) => r.duration_ms ?? 0).filter((v: number) => v > 0);
      const toSec = (ms: number) => Math.round((ms / 1000) * 10) / 10;
      const avg = vals.length > 0 ? toSec(vals.reduce((a: number, b: number) => a + b, 0) / vals.length) : 0;
      const min = vals.length > 0 ? toSec(Math.min(...vals)) : 0;
      const max = vals.length > 0 ? toSec(Math.max(...vals)) : 0;
      return {
        label: suiteRun.label,
        value: avg,
        min,
        max,
        errorRange: [avg - min, max - avg] as [number, number],
        count: vals.length,
        fill: runColors[i % runColors.length],
      };
    });
  }

  const scale = metricKey === 'confidence' ? 100 : 1;
  return suiteRuns.map((suiteRun, i) => {
    const vals: number[] = [];
    for (const run of suiteRun.runs) {
      const results = run.results as Record<string, unknown> | undefined;
      const v = results?.[metricKey];
      if (typeof v === 'number') vals.push(v);
    }
    const avg = vals.length > 0 ? (vals.reduce((a, b) => a + b, 0) / vals.length) * scale : 0;
    const min = vals.length > 0 ? Math.min(...vals) * scale : 0;
    const max = vals.length > 0 ? Math.max(...vals) * scale : 0;
    const rounded = Math.round(avg * 100) / 100;
    return {
      label: suiteRun.label,
      value: rounded,
      min: Math.round(min * 100) / 100,
      max: Math.round(max * 100) / 100,
      errorRange: [rounded - Math.round(min * 100) / 100, Math.round(max * 100) / 100 - rounded] as [number, number],
      count: vals.length,
      fill: runColors[i % runColors.length],
    };
  });
}

function metricDisplayLabel(key: string): string {
  if (key === 'pass_rate') return 'Pass Rate';
  if (key === 'duration') return 'Avg Duration';
  return formatMetricLabel(key);
}

function metricUnitForKey(key: string): string {
  if (key === 'pass_rate' || key === 'confidence') return '%';
  if (key === 'duration') return 's';
  return metricUnit(key);
}

function formatTickValue(v: number, unit: string): string {
  if (unit === 'ms' && Math.abs(v) >= 60000) return `${(v / 60000).toFixed(1)}m`;
  if (unit === 'ms' && Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}s`;
  if (Math.abs(v) >= 1000000) return `${(v / 1000000).toFixed(1)}M${unit}`;
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}k${unit}`;
  return `${v}${unit}`;
}

const MAX_PANELS = 6;

export function MetricDashboard({
  suiteRuns,
  cacheKey,
  className,
}: {
  suiteRuns: SuiteRunGroup[];
  cacheKey?: string | null;
  className?: string;
}) {
  const { resolvedTheme } = useTheme();
  const runColors = resolvedTheme === 'dark' ? RUN_COLORS_DARK : RUN_COLORS_LIGHT;
  const metricOptions = React.useMemo(() => discoverMetricOptions(suiteRuns), [suiteRuns]);

  const [panels, setPanels] = React.useState<DashboardPanel[]>(() => {
    if (cacheKey) {
      try {
        const raw = localStorage.getItem(`metric-panels::${cacheKey}`);
        if (raw) {
          const keys = JSON.parse(raw) as string[];
          const valid = keys.filter((k) => metricOptions.includes(k));
          if (valid.length > 0) return valid.map((key) => ({ id: nextPanelId(), metricKey: key }));
        }
      } catch {
        // fall through to defaults
      }
    }
    const defaults = metricOptions.slice(0, 2);
    return defaults.map((key) => ({ id: nextPanelId(), metricKey: key }));
  });

  // Persist selected metric keys when panels change
  React.useEffect(() => {
    if (!cacheKey) return;
    const keys = panels.map((p) => p.metricKey);
    localStorage.setItem(`metric-panels::${cacheKey}`, JSON.stringify(keys));
  }, [panels, cacheKey]);

  const addPanel = React.useCallback(() => {
    setPanels((prev) => {
      if (prev.length >= MAX_PANELS) return prev;
      const used = new Set(prev.map((p) => p.metricKey));
      const next = metricOptions.find((m) => !used.has(m)) ?? metricOptions[0];
      return [...prev, { id: nextPanelId(), metricKey: next }];
    });
  }, [metricOptions]);

  const removePanel = React.useCallback((id: string) => {
    setPanels((prev) => (prev.length <= 1 ? prev : prev.filter((p) => p.id !== id)));
  }, []);

  const changeMetric = React.useCallback((id: string, metricKey: string) => {
    setPanels((prev) => prev.map((p) => (p.id === id ? { ...p, metricKey } : p)));
  }, []);

  if (metricOptions.length === 0) return null;

  return (
    <div className={cn('space-y-2', className)}>
      <div className="grid grid-cols-2 gap-3">
        {panels.map((panel) => {
          const data = aggregateMetric(suiteRuns, panel.metricKey, runColors);
          const unit = metricUnitForKey(panel.metricKey);
          const chartConfig: ChartConfig = { value: { label: metricDisplayLabel(panel.metricKey) } };

          return (
            <div
              key={panel.id}
              className="rounded-md border border-gray-200 bg-white p-3 dark:border-white/10 dark:bg-white/5"
            >
              <div className="mb-2 flex items-center gap-2">
                <Select value={panel.metricKey} onValueChange={(v) => changeMetric(panel.id, v)}>
                  <SelectTrigger className="h-7 w-[180px] text-[12px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {metricOptions.map((m) => (
                      <SelectItem key={m} value={m} className="text-[12px]">
                        {metricDisplayLabel(m)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex-1" />
                {panels.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePanel(panel.id)}
                    className="cursor-pointer rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10"
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </div>
              <ChartContainer config={chartConfig} className="w-full" style={{ height: 160 }}>
                <ComposedChart data={data} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
                  <CartesianGrid
                    strokeDasharray="2 4"
                    vertical={false}
                    stroke="#d4d4d8"
                    className="dark:stroke-white/[0.06]"
                  />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={6}
                    className="text-[10px] text-gray-500"
                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '10px' }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={4}
                    className="text-[10px] text-gray-500"
                    width={56}
                    tickFormatter={(v) => formatTickValue(v, unit)}
                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '10px' }}
                  />
                  <ChartTooltip
                    cursor={
                      <Rectangle
                        fill="black"
                        fillOpacity={0.04}
                        stroke="none"
                        radius={2}
                        className="dark:fill-white/[0.06]"
                      />
                    }
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload as MetricDataPoint;
                      return (
                        <div className="z-50 min-w-[140px] rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-md dark:border-white/10 dark:bg-gray-900">
                          <div className="mb-2 border-gray-100 border-b pb-1.5 dark:border-white/10">
                            <span className="font-mono text-[13px] text-gray-900 dark:text-gray-100">{p.label}</span>
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <div className="flex items-center justify-between gap-4">
                              <span className="text-[12px] text-gray-500 dark:text-gray-400">Avg</span>
                              <span className="font-medium text-[12px] text-gray-900 dark:text-gray-100">
                                {p.value}
                                {unit}
                              </span>
                            </div>
                            {p.min !== p.max && (
                              <div className="flex items-center justify-between gap-4">
                                <span className="text-[12px] text-gray-500 dark:text-gray-400">Range</span>
                                <span className="font-medium text-[12px] text-gray-900 dark:text-gray-100">
                                  {p.min}
                                  {unit} - {p.max}
                                  {unit}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center justify-between gap-4">
                              <span className="text-[12px] text-gray-500 dark:text-gray-400">Scenarios</span>
                              <span className="font-medium text-[12px] text-gray-900 dark:text-gray-100">
                                {p.count}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={48}>
                    <ErrorBar dataKey="errorRange" width={8} strokeWidth={1.5} stroke="#6b7280" />
                    {data.map((entry) => (
                      <Cell key={entry.label} fill={entry.fill} />
                    ))}
                  </Bar>
                  {data.length > 1 && (
                    <Line
                      dataKey="value"
                      type="monotone"
                      stroke="#9ca3af"
                      strokeWidth={1.5}
                      strokeDasharray="4 3"
                      dot={false}
                      activeDot={false}
                      legendType="none"
                      tooltipType="none"
                    />
                  )}
                </ComposedChart>
              </ChartContainer>
            </div>
          );
        })}
      </div>
      {panels.length < MAX_PANELS && metricOptions.length > panels.length && (
        <button
          type="button"
          onClick={addPanel}
          className="flex h-7 cursor-pointer items-center gap-1.5 rounded-md border border-gray-300 border-dashed px-3 text-[12px] text-gray-500 transition-colors hover:border-gray-400 hover:bg-gray-50 hover:text-gray-700 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500 dark:hover:bg-white/5 dark:hover:text-gray-300"
        >
          <Plus className="size-3" />
          Add chart
        </button>
      )}
    </div>
  );
}
